import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Phone, Mail, Save, Edit2, CheckCircle2, XCircle,
  Camera, Calendar, Trophy, Star, TrendingUp, BarChart2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import SportIcon, { SPORT_LABELS } from '@/components/SportIcon';
import { toast } from 'sonner';

const _isValidPhone = (v) => {
  if (!v) return true;
  if (/^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]/.test(v)) return false; // reject dates
  return v.replace(/\D/g, '').length >= 10;
};

const editSchema = z.object({
  first_name:    z.string().min(1, 'First name is required'),
  last_name:     z.string().optional().or(z.literal('')),
  gender:        z.enum(['male', 'female', 'other']),
  dob:           z.string().optional().or(z.literal('')),
  contact_phone: z.string()
    .refine(v => !v || _isValidPhone(v), 'Mobile number must be at least 10 digits (not a date)')
    .optional().or(z.literal('')),
  contact_email: z.string().email('Invalid email').optional().or(z.literal('')),
});

function calcAge(dob) {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    if (isNaN(d)) return null;
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  } catch { return null; }
}

const SPORT_ICONS_PROFILE = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽', default:'🏅',
};

export default function PlayerProfilePage() {
  const { playerId }     = useParams();
  const navigate         = useNavigate();
  const location         = useLocation();
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';

  const [player, setPlayer]   = useState(location.state?.player || null);
  const [loading, setLoading] = useState(!location.state?.player);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoB64, setPhotoB64]         = useState(null);
  const [selectedSports, setSelectedSports] = useState([]); // admin-editable sports list

  const ALL_SPORTS = ['table_tennis','badminton','chess','carrom','pickleball'];

  // Phone normaliser — shared across save and inline formatting
  const normPhone = (p) => {
    if (!p) return p;
    const digits = p.replace(/\D/g,'');
    return digits.length === 10 ? `+91${digits}` : digits.length === 12 && digits.startsWith('91') ? `+${digits}` : p;
  };
  const [stats, setStats]               = useState(null);
  const [history, setHistory]           = useState(null);
  const [matchData, setMatchData]       = useState(null);   // individual match records
  const [expandedSport, setExpandedSport] = useState(null); // clicked sport
  const fileRef = useRef(null);

  // Only the player themselves (phone match) or admins can edit
  const isOwnProfile = player && user?.phone && (
    player.contact_phone === user.phone ||
    user?.memberships?.[0]?.roles?.some(r => ['org_owner','org_admin','tournament_manager','admin'].includes(r)) ||
    user?.super_admin
  );

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: zodResolver(editSchema),
  });

  useEffect(() => {
    if (location.state?.player) {
      const p = location.state.player;
      reset({
        first_name:    p.first_name || '',
        last_name:     p.last_name  || '',
        gender:        p.gender     || 'male',
        dob:           p.dob        || '',
        contact_phone: p.contact_phone || '',
        contact_email: p.contact_email || '',
      });
      setPhotoPreview(p.photo_url || null);
    } else {
      fetchPlayer();
    }
    fetchStats();
    fetchHistory();
    fetchMatches();
  }, [playerId, orgSlug]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/${playerId}/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (_) {}
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/${playerId}/history`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setHistory(await res.json());
    } catch (_) {}
  };

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/${playerId}/matches`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setMatchData(await res.json());
    } catch (_) {}
  };

  const fetchPlayer = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orgs/${orgSlug}/players`, { params: { limit: 500 } });
      const list = Array.isArray(res.data) ? res.data : res.data.items || [];
      const found = list.find(p => p.id === playerId);
      if (found) {
        setPlayer(found);
        setPhotoPreview(found.photo_url || null);
        reset({
          first_name:    found.first_name || '',
          last_name:     found.last_name  || '',
          gender:        found.gender     || 'male',
          dob:           found.dob        || '',
          contact_phone: found.contact_phone || '',
          contact_email: found.contact_email || '',
        });
      }
    } catch { toast.error('Failed to load player'); }
    finally { setLoading(false); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setPhotoPreview(ev.target.result); setPhotoB64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSave = async (data) => {
    setSaving(true);
    // Normalize phone
    const normPhone = (p) => {
      if (!p) return '';
      let d = p.replace(/[\s\-().]/g, '').trim();
      if (!d) return '';
      if (d.startsWith('+')) return d;
      if (/^91\d{10}$/.test(d)) return '+' + d;
      if (/^\d{10}$/.test(d)) return '+91' + d;
      return d;
    };
    try {
      const payload = {
        first_name:    data.first_name,
        last_name:     data.last_name     || undefined,
        gender:        data.gender,
        dob:           data.dob           || undefined,
        contact_phone: normPhone(data.contact_phone) || undefined,
        contact_email: data.contact_email || undefined,
        sports:        selectedSports,
        ...(photoB64 ? { photo_url: photoB64 } : {}),
      };
      await api.patch(`/orgs/${orgSlug}/players/${playerId}`, payload);
      toast.success('Profile updated');
      setEditing(false);
      setPlayer(prev => ({
        ...prev,
        first_name:    data.first_name,
        last_name:     data.last_name || prev?.last_name,
        full_name:     `${data.first_name} ${data.last_name || ''}`.trim(),
        gender:        data.gender,
        dob:           data.dob || prev?.dob,
        contact_phone: normPhone(data.contact_phone) || null,
        contact_email: data.contact_email || null,
        photo_url:     photoB64 || prev?.photo_url,
        sports:        selectedSports,
      }));
      if (photoB64) setPhotoPreview(photoB64);
      setPhotoB64(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-8 w-48 bg-[#1d2230] mb-6"/>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 bg-[#1d2230] rounded-xl"/>
          <Skeleton className="h-64 col-span-2 bg-[#1d2230] rounded-xl"/>
        </div>
      </div>
    );
  }

  if (!player) return (
    <div className="p-6 text-center">
      <p className="text-[#8a93a3]">Player not found</p>
      <Button onClick={() => navigate('/admin/players')} className="mt-4 bg-[#f5c518] text-[#0b0f18]">Back to Members</Button>
    </div>
  );

  const initials = `${player.first_name?.[0] || ''}${player.last_name?.[0] || ''}`.toUpperCase() || 'M';
  const age = calcAge(player.dob);
  const currentPhoto = photoPreview || player.photo_url;

  // Category badge
  const catLabel = player.category === 'champion' ? { label: '👑 Champion', color: '#f5c518' } :
                   player.category === 'advanced'  ? { label: '⭐ Advanced', color: '#3b82f6' } :
                   { label: player.category || 'Advanced', color: '#8a93a3' };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="player-profile-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/players')}
          className="text-[#8a93a3] hover:text-[#f0f2f7]">
          <ArrowLeft className="h-5 w-5"/>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7]">
            {player.full_name || `${player.first_name} ${player.last_name || ''}`.trim()}
          </h1>
          <p className="text-[#8a93a3] text-sm">Member Profile</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-6 text-center">
            {/* Photo */}
            <div className="relative mx-auto w-fit mb-4">
              <div className="w-24 h-24 rounded-full border-4 border-[#252a37] overflow-hidden flex items-center justify-center"
                style={{ background: currentPhoto ? 'transparent' : 'linear-gradient(135deg, #1d2230, #252a37)' }}>
                {currentPhoto
                  ? <img src={currentPhoto} alt={player.full_name} className="w-full h-full object-cover"/>
                  : <span className="text-3xl font-bold text-[#f5c518]">{initials}</span>
                }
              </div>
              {/* Camera button */}
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-[#f5c518] flex items-center justify-center hover:bg-[#f7d24a] transition-colors"
                title="Upload photo"
                data-testid="player-photo-upload-btn"
              >
                <Camera className="h-3.5 w-3.5 text-[#0b0f18]"/>
              </button>
              <input type="file" accept="image/*" ref={fileRef} hidden onChange={handlePhotoChange}/>
            </div>

            <h2 className="text-xl font-bold text-[#f0f2f7] mb-1">
              {player.full_name || player.first_name}
            </h2>

            {/* Category badge */}
            <Badge className="border mb-4 text-xs"
              style={{ backgroundColor: `${catLabel.color}20`, color: catLabel.color, borderColor: `${catLabel.color}50` }}>
              {catLabel.label}
            </Badge>

            {/* Status */}
            <div className="mb-4">
              <Badge className={`border ${
                player.status === 'active'
                  ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]'
                  : 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]'
              }`}>
                {player.status === 'active' ? <><CheckCircle2 className="h-3 w-3 mr-1"/>Active</> : <><XCircle className="h-3 w-3 mr-1"/>Inactive</>}
              </Badge>
            </div>

            {/* Key stats */}
            <div className="space-y-2 text-sm">
              {age !== null && (
                <div className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-lg">
                  <span className="text-[#8a93a3] flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5"/>Age</span>
                  <span className="text-[#f5c518] font-bold text-base">{age} yrs</span>
                </div>
              )}
              {player.dob && (
                <div className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-lg">
                  <span className="text-[#8a93a3]">Date of Birth</span>
                  <span className="text-[#f0f2f7] text-xs">{new Date(player.dob + (player.dob.includes('T') ? '' : 'T00:00')).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</span>
                </div>
              )}
              {player.gender && (
                <div className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-lg">
                  <span className="text-[#8a93a3]">Gender</span>
                  <span className="text-[#f0f2f7] capitalize">{player.gender}</span>
                </div>
              )}
              {player.contact_phone && admin && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#1d2230] rounded-lg">
                  <Phone className="h-4 w-4 text-[#8a93a3] flex-shrink-0"/>
                  <span className="font-mono text-xs text-[#f0f2f7] truncate">{player.contact_phone}</span>
                </div>
              )}
              {player.contact_email && admin && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#1d2230] rounded-lg">
                  <Mail className="h-4 w-4 text-[#8a93a3] flex-shrink-0"/>
                  <span className="text-xs text-[#f0f2f7] truncate">{player.contact_email}</span>
                </div>
              )}
              {player.sports?.length > 0 && (
                <div className="px-3 py-2 bg-[#1d2230] rounded-lg">
                  <p className="text-xs text-[#8a93a3] mb-2 flex items-center gap-1.5">
                    <Trophy className="h-3.5 w-3.5"/>Sports Playing
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {player.sports.map(sp => (
                      <span key={sp} className="inline-flex items-center gap-1 text-xs bg-[#252a37] border border-[#333a4d] text-[#f0f2f7] px-2 py-1 rounded-lg font-medium">
                        <span>{SPORT_ICONS_PROFILE[sp] || '🏅'}</span>
                        <span className="capitalize">{sp.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(!player.sports || player.sports.length === 0) && (
                <div className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-lg">
                  <span className="text-[#8a93a3] flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5"/>Sports</span>
                  <span className="text-[#8a93a3] text-xs italic">None assigned</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats + Edit Panel */}
        <div className="lg:col-span-2 space-y-6">

          {/* Performance Stats Card */}
          {stats && (
            <Card className="bg-[#161a26] border-[#252a37]" data-testid="player-stats-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-5">
                  <BarChart2 className="h-5 w-5 text-[#f5c518]"/>
                  <h3 className="text-lg font-semibold text-[#f0f2f7]">Match Performance</h3>
                </div>

                {stats.overall.played === 0 ? (
                  <p className="text-sm text-[#8a93a3] text-center py-4">No match data yet</p>
                ) : (
                  <>
                    {/* Overall stat pills */}
                    <div className="grid grid-cols-4 gap-3 mb-6">
                      {[
                        { label: 'Played',  value: stats.overall.played,  color: '#8a93a3' },
                        { label: 'Won',     value: stats.overall.wins,    color: '#22c55e' },
                        { label: 'Lost',    value: stats.overall.losses,  color: '#ef4444' },
                        { label: 'Win %',   value: `${stats.overall.win_pct}%`, color: '#f5c518' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="bg-[#1d2230] rounded-xl p-3 text-center">
                          <p className="text-xl font-bold" style={{ color }}>{value}</p>
                          <p className="text-xs text-[#8a93a3] mt-0.5">{label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Per-sport performance bars — clickable to drill into matches */}
                    <div className="space-y-2">
                      {Object.entries(stats.per_sport).map(([sc, sp]) => {
                        const winPct = sp.played > 0 ? Math.round(sp.wins / sp.played * 100) : 0;
                        const isOpen = expandedSport === sc;
                        const sportMatches = (matchData?.matches || []).filter(m => m.sport_code === sc);
                        const RESULT_COLORS = { win:'#22c55e', loss:'#ef4444', draw:'#f5c518', bye:'#8a93a3' };
                        return (
                          <div key={sc} data-testid={`sport-stat-${sc}`} className="rounded-xl overflow-hidden border border-[#252a37]">
                            {/* Clickable sport row */}
                            <button
                              onClick={() => setExpandedSport(isOpen ? null : sc)}
                              className="w-full flex flex-col gap-1.5 p-3 hover:bg-[#1d2230] transition-colors text-left">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-base">{SPORT_ICONS_PROFILE[sc] || '🏅'}</span>
                                  <span className="text-sm font-medium text-[#f0f2f7] capitalize">
                                    {sc.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-[#22c55e] font-semibold">{sp.wins}W</span>
                                  <span className="text-[#ef4444] font-semibold">{sp.losses}L</span>
                                  <span className="font-bold text-[#f5c518]">{winPct}%</span>
                                  <svg className={`h-3.5 w-3.5 text-[#8a93a3] transition-transform ${isOpen?'rotate-90':''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                </div>
                              </div>
                              <div className="h-1.5 bg-[#1d2230] rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700"
                                  style={{ width: `${winPct}%`, backgroundColor: winPct >= 60 ? '#22c55e' : winPct >= 40 ? '#f5c518' : '#ef4444' }}/>
                              </div>
                            </button>

                            {/* Expanded individual match records */}
                            {isOpen && (
                              <div className="border-t border-[#252a37] bg-[#0f1219]">
                                {sportMatches.length === 0 ? (
                                  <p className="text-[10px] text-[#8a93a3] px-3 py-2 text-center">No individual match records for this sport</p>
                                ) : (
                                  <div className="divide-y divide-[#1e2435]/60 max-h-72 overflow-y-auto">
                                    {sportMatches.map((m, i) => {
                                      const rc = RESULT_COLORS[m.result] || '#8a93a3';
                                      const isKO = ['sf','final','3rd'].includes(m.round_type);
                                      const scoreStr = m.my_score != null && m.opp_score != null ? `${m.my_score}–${m.opp_score}` : null;
                                      const games = (m.games || []).filter(g => g.home || g.away);
                                      return (
                                        <div key={i} className="flex items-center gap-2 px-3 py-2.5 text-xs">
                                          <div className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center font-black text-xs border"
                                            style={{ color: rc, borderColor: `${rc}40`, backgroundColor: `${rc}10` }}>
                                            {m.result === 'win' ? 'W' : m.result === 'loss' ? 'L' : m.result === 'draw' ? 'D' : 'B'}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                              <p className="text-[#f0f2f7] font-semibold truncate">
                                                vs {m.opp_player || m.opp_team || 'Opponent'}
                                              </p>
                                              {isKO && (
                                                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0"
                                                  style={{ color: m.round_type==='final'?'#fb923c':'#f5c518', backgroundColor: m.round_type==='final'?'rgba(251,146,60,0.1)':'rgba(245,197,24,0.1)' }}>
                                                  {m.round_type === 'final' ? 'FINAL' : 'SEMI'}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-[#8a93a3] truncate">{m.slot} · {m.opp_team}</p>
                                          </div>
                                          <div className="text-right flex-shrink-0">
                                            {scoreStr && <p className="font-black tabular-nums" style={{ color: rc }}>{scoreStr}</p>}
                                            {games.length > 0 && (
                                              <p className="text-[9px] text-[#8a93a3] font-mono">{games.map(g=>`${g.home}-${g.away}`).join(' ')}</p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Tournament History ──────────────────────────────── */}
          {history && history.tournaments?.length > 0 && (
            <Card className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Trophy className="h-5 w-5 text-[#f5c518]"/>
                  <h3 className="text-lg font-semibold text-[#f0f2f7]">Tournament History</h3>
                </div>
                <div className="space-y-4">
                  {history.tournaments.map(t => (
                    <div key={t.tournament_id} className="bg-[#1d2230] rounded-xl border border-[#252a37] overflow-hidden">
                      {/* Tournament header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-[#252a37]">
                        <div>
                          <p className="font-bold text-[#f0f2f7] text-sm">{t.tournament_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="h-4 w-4 rounded flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
                              style={{ backgroundColor: t.team_color || '#3b82f6' }}>
                              {t.team_name?.[0]}
                            </div>
                            <span className="text-xs text-[#8a93a3]">{t.team_name}</span>
                            {t.season && <span className="text-[10px] text-[#8a93a3]/60">· {t.season}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          {t.bid_amount ? (
                            <div className="text-right">
                              <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide">Bid</p>
                              <p className="text-sm font-black text-[#f5c518]">
                                ₹{t.bid_amount.toLocaleString('en-IN')}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {/* W/L/D stats */}
                      <div className="grid grid-cols-4 divide-x divide-[#252a37]">
                        {[
                          { label:'Played', val: t.played,  color:'#8a93a3' },
                          { label:'Wins',   val: t.wins,    color:'#22c55e' },
                          { label:'Losses', val: t.losses,  color:'#ef4444' },
                          { label:'Draws',  val: t.draws,   color:'#f5c518' },
                        ].map(({ label, val, color }) => (
                          <div key={label} className="text-center py-2.5">
                            <p className="text-base font-black tabular-nums" style={{ color }}>{val}</p>
                            <p className="text-[9px] text-[#8a93a3] uppercase tracking-wide">{label}</p>
                          </div>
                        ))}
                      </div>
                      {/* Per-sport breakdown */}
                      {Object.keys(t.sport_stats || {}).length > 0 && (
                        <div className="px-4 py-2 border-t border-[#252a37] flex flex-wrap gap-2">
                          {Object.entries(t.sport_stats).map(([sc, ss]) => (
                            <span key={sc} className="flex items-center gap-1 text-[10px] bg-[#161a26] border border-[#252a37] px-2 py-1 rounded-lg">
                              <span>{SPORT_ICONS_PROFILE[sc] || '🏅'}</span>
                              <span className="text-[#22c55e] font-bold">{ss.wins}W</span>
                              <span className="text-[#8a93a3]">{ss.losses}L</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-[#161a26] border-[#252a37]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#f0f2f7]">Profile Details</h3>
                {isOwnProfile && (!editing ? (
                  <Button variant="outline" size="sm" onClick={() => {
                    setSelectedSports(player.sports || []);
                    setEditing(true);
                  }}
                    className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]"
                    data-testid="player-edit-button">
                    <Edit2 className="h-4 w-4 mr-2"/>Edit
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setPhotoB64(null); setPhotoPreview(player.photo_url || null); }}
                    className="text-[#8a93a3] hover:text-[#f0f2f7]">
                    Cancel
                  </Button>
                ))}
              </div>

              {editing && isOwnProfile ? (
                <form onSubmit={handleSubmit(handleSave)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8a93a3]">First Name *</Label>
                      <Input {...register('first_name')} className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
                      {errors.first_name && <p className="text-xs text-[#ef4444] mt-1">{errors.first_name.message}</p>}
                    </div>
                    <div>
                      <Label className="text-[#8a93a3]">Last Name</Label>
                      <Input {...register('last_name')} className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8a93a3]">Date of Birth</Label>
                      <Input {...register('dob')} type="date"
                        className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                        data-testid="player-dob-input"/>
                      {errors.dob && <p className="text-xs text-[#ef4444] mt-1">{errors.dob.message}</p>}
                    </div>
                    <div>
                      <Label className="text-[#8a93a3]">Gender</Label>
                      <Controller name="gender" control={control} render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"><SelectValue/></SelectTrigger>
                          <SelectContent className="bg-[#161a26] border-[#252a37]">
                            <SelectItem value="male" className="text-[#f0f2f7] focus:bg-[#1d2230]">Male</SelectItem>
                            <SelectItem value="female" className="text-[#f0f2f7] focus:bg-[#1d2230]">Female</SelectItem>
                            <SelectItem value="other" className="text-[#f0f2f7] focus:bg-[#1d2230]">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      )}/>
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#8a93a3]">Phone</Label>
                    <Input {...register('contact_phone')} placeholder="+91 98765 43210"
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                      onBlur={e => {
                        const v = normPhone(e.target.value);
                        if (v !== e.target.value) { e.target.value = v; register('contact_phone').onChange({ target: e.target }); }
                      }}/>
                    {errors.contact_phone && <p className="text-xs text-[#ef4444] mt-1">{errors.contact_phone.message}</p>}
                  </div>
                  <div>
                    <Label className="text-[#8a93a3]">Email</Label>
                    <Input {...register('contact_email')} placeholder="player@example.com"
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/>
                    {errors.contact_email && <p className="text-xs text-[#ef4444] mt-1">{errors.contact_email.message}</p>}
                  </div>

                  {/* Sports selector */}
                  <div>
                    <Label className="text-[#8a93a3] mb-2 block">Sports</Label>
                    <div className="flex flex-wrap gap-2">
                      {ALL_SPORTS.map(sc => {
                        const selected = selectedSports.includes(sc);
                        return (
                          <button
                            key={sc}
                            type="button"
                            onClick={() => setSelectedSports(prev =>
                              prev.includes(sc) ? prev.filter(s => s !== sc) : [...prev, sc]
                            )}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                              selected
                                ? 'bg-[rgba(245,197,24,0.15)] border-[rgba(245,197,24,0.5)] text-[#f5c518]'
                                : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[#f5c518] hover:text-[#f5c518]'
                            }`}
                          >
                            <SportIcon sport={sc} size={14}/>
                            {SPORT_LABELS[sc] || sc}
                          </button>
                        );
                      })}
                    </div>
                    {selectedSports.length === 0 && (
                      <p className="text-[10px] text-[#8a93a3] mt-1">No sports assigned — click to select</p>
                    )}
                  </div>

                  {/* Photo section */}
                  <div>
                    <Label className="text-[#8a93a3] mb-2 block">Profile Photo</Label>
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-full overflow-hidden flex items-center justify-center bg-[#1d2230] border border-[#252a37] flex-shrink-0">
                        {(photoB64 || player.photo_url)
                          ? <img src={photoB64 || player.photo_url} className="h-full w-full object-cover" alt=""/>
                          : <span className="text-xl font-bold text-[#f5c518]">{initials}</span>
                        }
                      </div>
                      <div>
                        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                          className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]">
                          <Camera className="h-4 w-4 mr-2"/>
                          {(photoB64 || player.photo_url) ? 'Change Photo' : 'Upload Photo'}
                        </Button>
                        {photoB64 && <p className="text-xs text-[#22c55e] mt-1">New photo selected</p>}
                        <p className="text-xs text-[#8a93a3] mt-1">Max 2MB · JPG, PNG, WEBP</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="submit" disabled={saving}
                      className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                      data-testid="player-save-button">
                      {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2"/>Save Changes</>}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'First Name',     value: player.first_name },
                    { label: 'Last Name',      value: player.last_name || '—' },
                    { label: 'Date of Birth',  value: player.dob ? new Date(player.dob + (player.dob.includes('T') ? '' : 'T00:00')).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) : '—', highlight: !!age },
                    { label: 'Age',            value: age !== null ? `${age} years` : '—', highlight: !!age },
                    { label: 'Gender',         value: player.gender || '—', cap: true },
                    ...(admin ? [
                      { label: 'Phone',        value: player.contact_phone || '—', mono: true },
                      { label: 'Email',        value: player.contact_email || '—' },
                    ] : []),
                    { label: 'Category',       value: catLabel.label },
                  ].map(({ label, value, cap, mono, highlight }) => (
                    <div key={label} className="bg-[#1d2230] rounded-lg p-3">
                      <p className="text-xs text-[#8a93a3] mb-1">{label}</p>
                      <p className={`text-sm ${cap ? 'capitalize' : ''} ${mono ? 'font-mono' : ''} ${highlight ? 'text-[#f5c518] font-bold' : 'text-[#f0f2f7]'}`}>
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
